import { useMemo } from 'react';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import type { NullableDateInput } from '@localization/formatters/dateFormatter';
import { resolveEventDateDisplay } from '@shared/utils/eventDateDisplay/resolveEventDateDisplay';
import { eventDateMetadataToContent } from '@shared/utils/eventDateDisplay/eventDateMetadata';
// EVENT_DETAILS_SCHEMA_URL (string constant) and EventDetailsData (type-only) remain welcome-page component
// concerns and are guest-safe, but the no-restricted-imports rule still flags the @website-custom-components
// path for guest files — keep the suppression scoped to just these two.
/* eslint-disable no-restricted-imports */
import { EVENT_DETAILS_SCHEMA_URL } from '@website-custom-components/EventDetails/EventDetails.constants';
import type { EventDetailsData } from '@website-custom-components/EventDetails/EventDetails.types';
/* eslint-enable no-restricted-imports */
import { PageKind } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCustomData } from '../hooks/useCustomData';

type UseWelcomeEventDateDisplayArgs = Readonly<{
  eventHandle: string;
  eventId: string;
  startTimestamp: NullableDateInput;
  timezone?: string | null;
  // Event-level date config (PRNT-4548). Used instead of the welcome-page blob when the multi-day flag is on.
  dateMetadata?: Parameters<typeof eventDateMetadataToContent>[0];
  // The range END as an ISO-8601 timestamp (EventInfo.endDate.timestamp) — parseable by resolveEventDateDisplay.
  endDateTimestamp?: string | null;
}>;

// Returns the event's multi-day date override — a saved range or custom text (PRNT-4321) — so other
// surfaces (e.g. the registry header) render the event date the way the welcome page does. Returns
// undefined when the event has no saved range, so callers keep their existing single date (purely additive).
// Source is flag-gated: event-level dateMetadata/endDate when the multi-day flag is on, else the
// welcome-page JSON blob (PRNT-4548).
export const useWelcomeEventDateDisplay = ({ eventHandle, eventId, startTimestamp, timezone, dateMetadata, endDateTimestamp }: UseWelcomeEventDateDisplayArgs) => {
  const [isMultiDayEnabled, isFlagLoading] = useIsFeatureEnabled('websiteMultiDayEventDateEnabled');
  // Skip the blob fetch when the flag is on — and also while it's still resolving. This no-default remote
  // flag reads `false` during load, so without the isFlagLoading guard a cold registry load would start the
  // welcome-page blob query (and briefly read the legacy source) before the flag-on value can switch it off.
  // Empty slug → useGuestPageContainerBySlugQuery skips.
  const { pageContainerBySlug, loading } = useCustomData(eventId, eventHandle, isMultiDayEnabled || isFlagLoading ? '' : 'welcome');
  const { formatDate, formatEventDateRange } = useEventLocaleFormatter();

  const content = useMemo<EventDetailsData | undefined>(() => {
    if (isMultiDayEnabled) {
      return eventDateMetadataToContent(dateMetadata, endDateTimestamp);
    }
    // Only trust the system/migrated welcome page — a user-created custom page squatting the 'welcome'
    // slug (pageKind userCustom) must not override the registry date. Mirrors the systemCustom guard
    // used for the registry page container in GuestRegistry.controller.
    if (pageContainerBySlug?.pageKind !== PageKind.systemCustom) {
      return undefined;
    }
    const block = pageContainerBySlug.components?.find(
      component => component.__typename === 'JsonDataBlobComponent' && component.containerContent?.schemaUrl === EVENT_DETAILS_SCHEMA_URL,
    );
    return block?.__typename === 'JsonDataBlobComponent' ? (block.containerContent.content as EventDetailsData) : undefined;
  }, [isMultiDayEnabled, dateMetadata, endDateTimestamp, pageContainerBySlug]);

  const options = { titlecase: true, timezone };
  // Flag on: the event-level source (PRNT-4548) carries both the single-date format and the range, so
  // resolve either — a couple who saved e.g. MM/dd/yyyy must see it on the registry too. Flag off (legacy
  // blob): only a saved range / custom text produces an override; single-date events keep the caller's
  // default formatted date, matching prior behavior. (For an unmigrated event-level source dateFormat is
  // undefined, so resolveEventDateDisplay returns the same default string the caller already falls back to.)
  const shouldResolveEventDate = content && (isMultiDayEnabled || content.dateDisplayType === 'dateRange');
  const eventDateDisplay = shouldResolveEventDate ? resolveEventDateDisplay(content, startTimestamp, options, { formatDate, formatEventDateRange }) : undefined;

  // Still "loading" while the flag resolves — the source (and thus any override) isn't settled yet.
  return { eventDateDisplay, loading: isFlagLoading || (isMultiDayEnabled ? false : loading) };
};
